<script setup>
import { reactive, computed } from 'vue';
import { Alert, Card, Toggle, Hints, Heading, SectionHead, Suggestions, Buttons, Button, Radio, Color, Icon, Uploader, Input, Range, Preview, DarkModeSwitch } from '@components'
import CallToAction from './CallToAction.vue'

const state = reactive({
    isActive: false,
})

import DarkModeStore from '@o/store'
const { options, isPro } = DarkModeStore()

import { getRange, isSwitchLocked } from '@o/helper'

// setFromRange.
const setFromRange = (value, key) => {
    options[key] = value;
}

const previewConfig = computed(() => {
    return {
        textLight: options.floating_switch_enabled_custom_texts ? options.floating_switch_text_light : 'Light',
        textDark: options.floating_switch_enabled_custom_texts ? options.floating_switch_text_dark : 'Dark',
        iconLight: options.floating_switch_enabled_custom_icons ? options.floating_switch_icon_light : '',
        iconDark: options.floating_switch_enabled_custom_icons ? options.floating_switch_icon_dark : '',
        size: switchSize.value,
    }
})

const switchPosition = computed(() => {

    if (options.floating_switch_position === 'custom') {
        return `bottom: ${options.floating_switch_position_bottom_value}px; ${options.floating_switch_position_side} : ${options.floating_switch_position_side_value}px;`;
    }

    if (options.floating_switch_position === 'left') {
        return 'left: 10px; bottom: 10px;';
    }

    return 'right: 10px; bottom: 10px;';
})

const switchSize = computed(() => {

    if (options.floating_switch_size === 'custom') {
        return options.floating_switch_size_custom / 100;
    }

    return options.floating_switch_size;
})


</script>

<template>
    <Card borderless transparent class="gap-5">
        <Alert color="yellow" v-if="!options.frontend_enabled"> The Floating Switch is <strong>currently
                unavailable</strong>. To access this feature, first you have to enable the “Frontend Dark Mode” feature from
            the Control settings <template #button>
                <RouterLink to="/frontend"><Button color="yellow"
                        class="text-xs whitespace-nowrap bg-[#F59E0B] border-[#F59E0B]">Enable Frontend Dark Mode
                        <Icon name="chevronRight" />
                    </Button></RouterLink>
            </template>
        </Alert>
        <Card :disabled="!options.frontend_enabled">
            <Toggle v-model="options.floating_switch_enabled">Display Floating Switch</Toggle>
            <Hints>Enable Floating dark mode switch in frontend for toggling between dark and light mode.</Hints>
        </Card>
        <!-- if floating switch is enabled  -->
        <Card borderless transparent class="gap-5" v-if="options.floating_switch_enabled"
            :disabled="!options.frontend_enabled">
            <SectionHead> Floating Switch Styles <template #description>Choose the style of the floating toggle switch</template>
            </SectionHead>
            
            <!-- switch styles -->
            <Card class="gap-6">
                <Card transparent borderless>
                    <Heading>Normal Switches</Heading>
                    <div class="flex flex-wrap gap-3">
                        <div v-for="n in getRange(1, 13)" :key="n" tabindex="0"
                            :class="{ '_selected': options.floating_switch_style === n, 'wp-dark-mode-locked bg-[#fdfaf8] ring-1 ring-[#ffedd5]': isSwitchLocked(n), 'bg-[#F9FAFB]' : !isSwitchLocked(n) }"
                            @click.prevent="isSwitchLocked(n) ? '' : setFromRange(n, 'floating_switch_style');"
                            class="flex flex-col items-center justify-center transition duration-75  rounded-lg relative cursor-pointer w-36 h-36">
                            <DarkModeSwitch :id="n" :class="{ 'opacity-60 pointer-events-none': isSwitchLocked(n) }"
                                dummy="true" />
                            <ProBadge class="absolute right-2 top-2" text="" v-if="isSwitchLocked(n)" />
                        </div>
                    </div>
                </Card>
                <div class="flex flex-col gap-2">
                    <div class="flex sm:items-center sm:justify-between flex-col sm:flex-row">
                        <Heading id="accessibility-switches">Accessibility Switches</Heading>
                        <router-link to="/accessibility" class="flex items-center gap-2 text-sm"><svg
                                class="w-4 fill-current" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd"
                                    d="M7.99992 2.66665C5.0544 2.66665 2.66659 5.05446 2.66659 7.99998C2.66659 10.9455 5.0544 13.3333 7.99992 13.3333C10.9454 13.3333 13.3333 10.9455 13.3333 7.99998C13.3333 5.05446 10.9454 2.66665 7.99992 2.66665ZM1.33325 7.99998C1.33325 4.31808 4.31802 1.33331 7.99992 1.33331C11.6818 1.33331 14.6666 4.31808 14.6666 7.99998C14.6666 11.6819 11.6818 14.6666 7.99992 14.6666C4.31802 14.6666 1.33325 11.6819 1.33325 7.99998ZM7.99992 5.33331C8.36811 5.33331 8.66659 5.63179 8.66659 5.99998V7.33331H9.99992C10.3681 7.33331 10.6666 7.63179 10.6666 7.99998C10.6666 8.36817 10.3681 8.66665 9.99992 8.66665H8.66659V9.99998C8.66659 10.3682 8.36811 10.6666 7.99992 10.6666C7.63173 10.6666 7.33325 10.3682 7.33325 9.99998V8.66665H5.99992C5.63173 8.66665 5.33325 8.36817 5.33325 7.99998C5.33325 7.63179 5.63173 7.33331 5.99992 7.33331H7.33325V5.99998C7.33325 5.63179 7.63173 5.33331 7.99992 5.33331Z"
                                    fill="fill-current" />
                            </svg> More accessibility features</router-link>
                    </div>
                    <div class="flex flex-wrap justify-start gap-3">
                        <div v-for="n in getRange(14, 19)" :key="n" tabindex="0"
                            :class="{ '_selected': options.floating_switch_style === n, 'wp-dark-mode-locked bg-[#fdfaf8] ring-1 ring-[#ffedd5]': isSwitchLocked(n), 'bg-[#F9FAFB]' : !isSwitchLocked(n) }"
                            @click.prevent="isSwitchLocked(n) ? '' : setFromRange(n, 'floating_switch_style');"
                            class="flex flex-col items-center justify-center transition duration-75  rounded-lg relative cursor-pointer w-36 h-36">
                            <DarkModeSwitch :id="n" :class="{ 'opacity-60 pointer-events-none': isSwitchLocked(n) }"
                                dummy="true" />
                            <ProBadge class="absolute right-2 top-2" text="" v-if="isSwitchLocked(n)" />
                        </div>
                    </div>
                </div>
                <Alert color="yellow" class="text-xs"
                    v-if="options.floating_switch_style > 13 && !options.typography_enabled"> Typography option is disabled
                    in Accessibility Settings. Text scaling feature won’t work. <template #button>
                        <RouterLink to="/accessibility"><Button color="yellow"
                                class="text-xs whitespace-nowrap bg-[#F59E0B] border-[#F59E0B]">Go to Accessibility settings
                                <Icon name="chevronRight" />
                            </Button></RouterLink>
                    </template>
                </Alert>
            </Card>
            <!-- switch customization  -->
            <Card transparent borderless>
                <Heading>Switch Customization</Heading>
            </Card>
            <Card class="gap-8">
                <!-- Preview group  -->
                <div class="flex gap-6 justify-between flex-wrap">
                    <!-- left section of preview  -->
                    <div class="flex flex-col w-fit gap-6">
                        <!-- size  -->
                        <Card borderless>
                            <Heading>Floating Switch Size <Hints>Choose the size of the floating toggle switch</Hints>
                            </Heading>
                            <Buttons :options="$default.floating_switch.size.options" v-model="options.floating_switch_size"></Buttons>
                            <Card dark v-if="options.floating_switch_size === 'custom'" class="flex flex-col gap-2">
                                <Hints>Scale in percentage relative to normal size</Hints>
                                <Range default="100" :min="50" :max="300" step="1" placeholder="Custom size"
                                    v-model="options.floating_switch_size_custom">
                                </Range>
                            </Card>
                        </Card>
                        <!-- position  -->
                        <Card borderless>
                            <Heading>Switch Position <Hints>Choose the alignment of the floating toggle switch</Hints>
                            </Heading>
                            <Buttons v-model="options.floating_switch_position" :options="$default.floating_switch.position.options"
                                locked="['custom']"></Buttons>
                            <Card dark v-if="options.floating_switch_position === 'custom'" class="flex flex-col gap-2 w-fit">
                                <Heading>Side Selection</Heading>
                                <div class="flex items-center gap-2">
                                    <button v-for="side in ['left', 'right']" :class="{
                                        'text-blue-700 bg-blue-100': options.floating_switch_position_side === side,
                                        'text-gray-500 bg-gray-100': options.floating_switch_position_side !== side,
                                    }" @click.prevent="options.floating_switch_position_side = side"
                                        class="px-6 py-2 text-sm font-normal capitalize rounded-full shadow-sm">{{ side
                                        }}</Button>
                                </div>
                                <div class="grid grid-cols-1 gap-3">
                                    <div class="flex items-center gap-2">
                                        <Heading class="w-36">Spacing from bottom</Heading>
                                        <Input type="number" class="w-24" inline placeholder="10"
                                            v-model="options.floating_switch_position_bottom_value">
                                        <template #after><span class="text-gray-500 text-sm">px</span></template>
                                        </Input>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <Heading class="w-36">Spacing from {{ options.floating_switch_position_side }}
                                        </Heading>
                                        <Input type="number" class="w-24" inline placeholder="10"
                                            v-model="options.floating_switch_position_side_value">
                                        <template #after><span class="text-gray-500 text-sm">px</span></template>
                                        </Input>
                                    </div>
                                </div>
                            </Card>
                        </Card>
                        <!-- attention effect  -->
                        <Card borderless id="switch-animation">
                            <Card borderless transparent>
                                <Toggle v-model="options.floating_switch_enabled_attention_effect">Switch Attention Effect
                                </Toggle>
                                <Hints>Enable to choose an animation effect for the selected switch</Hints>
                            </Card>
                            <Card dark v-if="options.floating_switch_enabled_attention_effect" class="max-w-xs">
                                <Radio v-model="options.floating_switch_attention_effect" :options="$default.floating_switch.attention_effect.options"
                                    :locked="Object.keys($default.floating_switch.attention_effect.options).slice(2)">
                                </Radio>
                            </Card>
                        </Card>
                        <!-- call to action  -->
                        <Card borderless :data-wp-dark-mode-locked="!isPro">
                            <Card borderless transparent>
                                <Toggle v-model="options.floating_switch_enabled_cta" locked>Call-to-action Text </Toggle>
                                <Hints locked>Enable to add a call-to-action text for the dark mode switch.</Hints>
                            </Card>
                            <!-- call to action text, color and background -->
                            <Card dark class="gap-6 max-w-fit" v-if="options.floating_switch_enabled_cta && isPro" :data-wp-dark-mode-locked="!isPro">
                                <!-- call to action text  -->
                                <Input placeholder="Call-to-action Text" v-model="options.floating_switch_cta_text"
                                    label="Call-to-action Text"
                                    hints="Add call to action text beside the dark mode button" />
                                <!-- call to action color  -->
                                <Color v-model="options.floating_switch_cta_color" default="#F87171">
                                    <Heading class="mb-2">CTA Text Color <Hints>Color of the call to action text</Hints>
                                    </Heading>
                                </Color>
                                <!-- call to action color  -->
                                <Color v-model="options.floating_switch_cta_background" default="#FFFFFF">
                                    <Heading class="mb-2">CTA Background Color <Hints>Background of the switch button
                                        </Hints>
                                    </Heading>
                                </Color>
                            </Card>
                        </Card>
                        <!-- divider  -->
                        <div class="w-full max-w-xs h-0.5 rounded-full bg-gray-100" />
                        <!-- attention effect  -->
                        <Card borderless transparent :data-wp-dark-mode-locked="!isPro">
                            <Card transparent borderless>
                                <Toggle v-model="options.floating_switch_enabled_custom_icons" locked>Custom Switch Icon
                                </Toggle>
                                <Hints locked>Enable to use your custom switch icon.</Hints>
                            </Card>
                            <Card dark class="w-72" v-if="options.floating_switch_enabled_custom_icons">
                                <Heading>Light Mode Switch Icon</Heading>
                                <Uploader v-model="options.floating_switch_icon_light" type="image/*"></Uploader>
                                <Heading>Dark Mode Switch Icon</Heading>
                                <Uploader v-model="options.floating_switch_icon_dark" type="image/*"></Uploader>
                            </Card>
                        </Card>
                        <!-- custom switch texts  -->
                        <Card borderless transparent :data-wp-dark-mode-locked="!isPro">
                            <Card transparent borderless>
                                <Toggle v-model="options.floating_switch_enabled_custom_texts" locked>Custom Switch Text
                                </Toggle>
                                <Hints locked>Customize the dark mode switch text.</Hints>
                            </Card>
                            <Card dark class="w-72" v-if="options.floating_switch_enabled_custom_texts">
                                <Input v-model="options.floating_switch_text_light" placeholder="Light Mode Switch Text"
                                    label="Light Mode Switch Text" hints="Add custom text for the light mode switch" />
                                <Input v-model="options.floating_switch_text_dark" placeholder="Dark Mode Switch Text"
                                    label="Dark Mode Switch Text" hints="Add custom text for the dark mode switch" />
                            </Card>
                        </Card>
                    </div>
                   <div>
                     <!-- preview section  -->
                     <Preview class="w-fit hidden sm:flex sm:sticky sm:top-20" :dark="state.previewActive">
                        <div class="relative h-56">
                            <div class="absolute flex items-center gap-2"
                                :class="{ 'flex-row-reverse': options.floating_switch_position === 'left' }"
                                :style="`${switchPosition} --wp-dark-mode-switch-scale: ${switchSize}`">
                                <CallToAction v-if="options.floating_switch_enabled_cta" />
                                <!-- {{ previewConfig }} -->
                                <DarkModeSwitch
                                    :class="options.floating_switch_enabled_attention_effect ? 'wp-dark-mode-switch-effect-' + options.floating_switch_attention_effect.toLowerCase() : ''"
                                    :config="previewConfig" @click="state.previewActive = !state.previewActive;"
                                    :active="state.previewActive" :id="options.floating_switch_style" />
                                <!-- <CallToAction
                                    v-if="options.floating_switch_enabled_cta && options.floating_switch_position !== 'right'" /> -->
                            </div>
                        </div>
                    </Preview>
                   </div>
                </div>
            </Card>
        </Card>
        <Suggestions paths="shortcode, excludes, accessibility"></Suggestions>
    </Card>
</template>